import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorAvailableExamsService } from '../../../../../api/api-services/operator-available-exams.service';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { MOBILE_LAYOUT_QUERY } from '../../../../shared/services/mobile-shell.service';
import { IFasTableDropdownFilter, IFasTableFilters } from 'fas-services';
import { Component, OnInit, inject } from '@angular/core';

@Component({
  selector: 'app-category-list',
  templateUrl: './category-list.component.html',
  styleUrl: './category-list.component.scss',
})
export class CategoryListComponent implements OnInit {
  private _operatorElearningTopicCategoriesService = inject(
    OperatorElearningTopicCategoriesService
  );
  private _operatorAvailableExamsService = inject(OperatorAvailableExamsService);

  public originalCategoriesList: IOperatorElearningTopicCategoryVm[] = [];

  public selectedCategory!: IOperatorElearningTopicCategoryVm;
  public categoriesList: IOperatorElearningTopicCategoryVm[] = [];
  public isLoading = false;
  public categoryIndex = 0;
  public currentRows = 10;
  public currentFirst = 0;

  public dropdownFilters: IFasTableDropdownFilter[] = [];
  private _searchValue = '';
  private _selectedExamGuids: string[] = [];

  public detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  public categoryColumns = [
    {
      field: 'order',
      header: 'Kolejność',
      contentClass: 'text-sm',
      sortable:true,
    },
    {
      field: 'name',
      header: 'Nazwa działu',
      contentClass: 'text-sm',
      columnClass: 'name-col',
      sortable:true,
    },
    {
      field: 'examsInfo',
      header: 'Przypisane kategorie egzaminów',
      contentClass: 'text-sm',
      valueFunction: this.getExamNames.bind(this),
    },
    {
      field: 'isActive',
      header: 'Pokaż w aplikacji',
      booleanField:true,
      contentClass: 'text-sm',
    },
    {
      field: 'translationsInfo',
      header: 'Wersje językowe',
      contentClass: 'text-sm',
      valueFunction: this.getTranslationsInfo.bind(this),
    },
    {
      field: 'created',
      header: 'Data utworzenia',
      contentClass: 'text-sm',
      dateField: true,
    },
    {
      header: 'Akcje',
      contentClass: 'text-sm',
      actions: [
        {
          buttonClass:
            'pi pi-eye cursor-pointer text-center appear transparent-button',
          callback: this.editCategory.bind(this),
          tooltip: 'Podgląd',
        },
      ],
    },
  ];

  ngOnInit(): void {
    this.loadPaginationState();
    this.loadAvailableExams();
    this.loadCategories();
  }

  private loadAvailableExams(): void {
    this._operatorAvailableExamsService.get().subscribe({
      next: (exams) => {
        this.dropdownFilters = [
          {
            id: 'exams',
            placeholder: 'Filtruj po kategorii egzaminu',
            options: exams.map((e) => ({ label: e.examName, value: e.guid })),
          },
        ];
      },
      error: (error) => {
        this.dropdownFilters = [];
      },
    });
  }

  private loadCategories(): void {
    this.isLoading = true;
    this._operatorElearningTopicCategoriesService
      .get(this._searchValue || undefined, this._selectedExamGuids.length ? this._selectedExamGuids : undefined)
      .subscribe({
        next: (categories) => {
          this.originalCategoriesList = categories;
          this.categoriesList = categories;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        },
      });
  }

  public onFiltersChange(filters: IFasTableFilters): void {
    this._searchValue = filters.searchString ?? '';
    this._selectedExamGuids = filters.dropdownValues?.['exams'] ?? [];
    this.loadCategories();
  }

  public getExamNames(category: IOperatorElearningTopicCategoryVm): string {
    if (category.examCategories && category.examCategories.length > 0) {
      return category.examCategories
        .map((exam: any) => exam.examName)
        .join(', ');
    }
    return 'Brak przypisanych kategorii';
  }

  public getTranslationsInfo(
    category: IOperatorElearningTopicCategoryVm
  ): string {
    if (
      category.topicCategoryTranslations &&
      category.topicCategoryTranslations.length > 0
    ) {
      const nonPolishTranslations = category.topicCategoryTranslations.filter(
        (t: any) => t.languageCode !== 'pl'
      );

      if (nonPolishTranslations.length > 0) {
        const languages = nonPolishTranslations
          .map((t: any) => t.languageCode?.toUpperCase())
          .join(', ');
        return `${languages}`;
      } else {
        return 'Brak tłumaczeń';
      }
    }
    return 'Brak tłumaczeń';
  }

  /** On a phone the preview button is in the last column, off the screen: a tap on the row opens the category. */
  public onRowTap = (category: any, index: number): void => {
    if (window.matchMedia(MOBILE_LAYOUT_QUERY).matches) {
      this.editCategory(category, index);
    }
  };

  public editCategory(category: any, index: number): void {
    const originalCategory = this.originalCategoriesList.find(
      (c) => c.id === category.id
    );
    if (originalCategory) {
      this.selectedCategory = originalCategory;
      this.categoryIndex = this.originalCategoriesList.findIndex(
        (c) => c.id === category.id
      );
      this.detailsControl.isOpen = true;
      this.detailsControl.isEditing = false;
    }
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
  }

  public refreshCategories(): void {
    this.loadCategories();
  }

  public setCurrentPosition(event: any): void {
    this.currentFirst = event.first;
    this.currentRows = event.rows;
    this.savePaginationState();
  }

  private loadPaginationState(): void {
    const savedState = localStorage.getItem('elearning-category-pagination');
    if (savedState) {
      const state = JSON.parse(savedState);
      this.currentFirst = state.currentFirst || 0;
      this.currentRows = state.currentRows || 10;
    }
  }

  private savePaginationState(): void {
    const state = {
      currentFirst: this.currentFirst,
      currentRows: this.currentRows
    };
    localStorage.setItem('elearning-category-pagination', JSON.stringify(state));
  }
}
